<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg
						class="w-5 h-5 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						Growth & Outreach
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Analytics, newsletters and audience engagement
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<!-- ── Tab Navigation ── -->
		<div
			class="flex gap-1 p-1 mb-6 bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 rounded-xl w-fit">
			<button
				v-for="tab in tabs"
				:key="tab.key"
				@click="activeTab = tab.key"
				class="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold transition-all duration-200"
				:class="
					activeTab === tab.key
						? 'bg-white dark:bg-dark-700 text-primary-600 dark:text-primary-400 shadow-card'
						: 'text-muted dark:text-muted-light hover:text-dark-700 dark:hover:text-white'
				">
				<svg
					class="w-4 h-4"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						:d="tab.icon" />
				</svg>
				{{ tab.label }}
			</button>
		</div>

		<!-- ══════════ OUTREACH TAB ══════════ -->
		<div v-if="activeTab === 'outreach'" class="space-y-5">
			<!-- KPI cards -->
			<div
				v-if="showNewsletterKPIs"
				class="grid grid-cols-1 sm:grid-cols-3 gap-4">
				<div
					v-for="stat in statsCards"
					:key="stat.label"
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card p-5 border border-primary-100 dark:border-dark-600 flex items-center gap-4 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200">
					<div
						class="w-11 h-11 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
						<svg
							class="w-5 h-5 text-primary-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								:d="stat.icon" />
						</svg>
					</div>
					<div>
						<p
							class="font-display text-2xl font-extrabold text-dark-700 dark:text-white leading-none">
							{{ stat.value }}
						</p>
						<p class="text-[12px] text-muted dark:text-muted-light mt-0.5">
							{{ stat.label }}
						</p>
					</div>
				</div>
			</div>

			<!-- Newsletter CTA card -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-dashed border-primary-200 dark:border-primary-400/25 flex flex-col items-center py-14 px-8 text-center">
				<div
					class="w-14 h-14 rounded-2xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center mb-5">
					<svg
						class="w-7 h-7 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
					</svg>
				</div>

				<h2
					class="font-display text-[18px] font-bold text-dark-700 dark:text-white mb-3">
					Send Newsletter
				</h2>

				<p
					v-if="forAdmin()"
					class="text-[13px] text-muted dark:text-muted-light leading-relaxed max-w-xl mb-8">
					Engage your entire platform with powerful, centralized communication.
					Newsletters allow you to send platform-wide updates, announcements,
					and promotional campaigns to attendees and organizers while respecting
					user consent and preferences.
				</p>
				<p
					v-if="forClient()"
					class="text-[13px] text-muted dark:text-muted-light leading-relaxed max-w-xl mb-8">
					Stay connected with your audience beyond your events. Reach your past
					and upcoming attendees with updates, promotions, and important
					announcements — all from one place.
				</p>

				<!-- Steps -->
				<div
					class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8 w-full text-left">
					<div
						v-for="(step, i) in forAdmin() ? steps : organizerStep"
						:key="i"
						class="flex items-start gap-3 p-4 rounded-xl bg-primary-50/60 dark:bg-dark-600/60 border border-primary-100 dark:border-dark-500">
						<div
							class="w-7 h-7 rounded-full bg-primary-400 flex items-center justify-center text-[11px] font-extrabold text-white flex-shrink-0">
							{{ i + 1 }}
						</div>
						<div>
							<p
								class="text-[13px] font-semibold text-dark-700 dark:text-white">
								{{ step.title }}
							</p>
							<p
								class="text-[11px] text-muted dark:text-muted-light mt-0.5 leading-relaxed"
								v-html="step.desc" />
						</div>
					</div>
				</div>

				<button
					@click="openCreateCampaignModal"
					class="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-display font-bold text-[14px] bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200">
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2.5"
							d="M12 4v16m8-8H4" />
					</svg>
					Send Newsletter
				</button>
			</div>
		</div>

		<!-- ══════════ GROWTH TAB ══════════ -->
		<div v-if="activeTab === 'growth'" class="space-y-4">
			<div>
				<h2
					class="font-display text-[16px] font-bold text-dark-700 dark:text-white">
					Growth Analytics
				</h2>
				<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
					Track your event's growth and performance metrics
				</p>
			</div>
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card p-10 border border-primary-100 dark:border-dark-600 text-center">
				<div
					class="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center mx-auto mb-4">
					<svg
						class="w-6 h-6 text-muted dark:text-muted-light"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
					</svg>
				</div>
				<p
					class="font-display font-bold text-[14px] text-dark-700 dark:text-white mb-1">
					Coming Soon
				</p>
				<p class="text-[13px] text-muted dark:text-muted-light">
					Growth analytics are on the way. Stay tuned!
				</p>
			</div>
		</div>

		<!-- ══════════ CAMPAIGN MODAL ══════════ -->
		<transition name="modal">
			<div
				v-if="showCreateModal"
				class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
				@click.self="closeCreateCampaignModal">
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-dropdown w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-primary-100 dark:border-dark-600">
					<!-- Header -->
					<div
						class="flex items-center justify-between p-6 border-b border-primary-100 dark:border-dark-600">
						<div>
							<p
								class="text-[10px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-0.5">
								Outreach
							</p>
							<h3
								class="font-display text-[16px] font-bold text-dark-700 dark:text-white">
								Create New Campaign
							</h3>
						</div>
						<button
							@click="closeCreateCampaignModal"
							class="w-8 h-8 rounded-xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center text-muted dark:text-muted-light hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors duration-150">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2.5"
									d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>

					<!-- Form -->
					<form @submit.prevent="confirmCreateCampaign" class="p-6 space-y-5">
						<!-- Communication type -->
						<div>
							<label class="modal-label">
								Communication Type
								<span class="text-red-400 normal-case text-[10px]"
									>required</span
								>
							</label>
							<select v-model="sendingMethod" required class="modal-input">
								<option value="" disabled selected>
									Choose communication type
								</option>
								<option
									v-for="type in communicationTypes"
									:key="type.value"
									:value="type.value">
									{{ type.label }}
								</option>
							</select>
						</div>

						<!-- Target audience -->
						<div>
							<label class="modal-label">
								Target Audience
								<span class="text-red-400 normal-case text-[10px]"
									>required</span
								>
							</label>
							<select
								v-model="sendingDestination"
								required
								class="modal-input"
								v-if="forAdmin()">
								<option
									v-for="option in audienceOptions"
									:key="option.value"
									:value="option.value">
									{{ option.label }}
								</option>
							</select>
							<select
								v-model="sendingDestination"
								required
								class="modal-input"
								v-if="forClient()">
								<option>Select target audience</option>
								<option value="1">All attendees</option>
							</select>
						</div>

						<!-- Subject -->
						<div>
							<label class="modal-label">
								Subject
								<span class="text-red-400 normal-case text-[10px]"
									>required</span
								>
							</label>
							<input
								type="text"
								v-model="form.subject"
								required
								class="modal-input"
								placeholder="e.g., Don't miss our summer event!" />
						</div>

						<!-- Message -->
						<div>
							<label class="modal-label"
								>Message Content
								<span class="text-red-400 normal-case text-[10px]"
									>required</span
								></label
							>
							<QuillEditor
								ref="quillEditor"
								contentType="html"
								v-model:content="form.messageContent"
								:options="{
									theme: 'snow',
									placeholder: 'Write your message here...',
									modules: {
										toolbar: [
											['bold', 'italic', 'underline', 'strike'],
											[{ list: 'ordered' }, { list: 'bullet' }],
											['clean'],
										],
									},
								}" />
						</div>

						<!-- Actions -->
						<div
							class="flex justify-end gap-3 pt-2 border-t border-primary-100 dark:border-dark-600">
							<button
								type="button"
								@click="closeCreateCampaignModal"
								class="px-5 py-2.5 rounded-xl text-[13px] font-semibold bg-primary-50 dark:bg-dark-600 text-dark-400 dark:text-white/70 border border-primary-100 dark:border-dark-500 hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors">
								Cancel
							</button>
							<button
								type="submit"
								class="px-5 py-2.5 rounded-xl text-[13px] font-bold bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200">
								Create Newsletter
							</button>
						</div>
					</form>
				</div>

				<!-- Confirm sub-dialog -->
				<CustomConfirmDialog
					:visible="showConfirmDialog"
					title="Confirm Newsletter Creation"
					message="Are you sure you want to send this newsletter?"
					@confirm="createCampaign"
					@cancel="showConfirmDialog = false" />
			</div>
		</transition>
	</div>
</template>

<script>
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import CustomConfirmDialog from "@/components/CustomConfirmDialog.vue";
import { apiDataService } from "@/services/apiDataService";
import { showToast } from "@/constants/toast";
import { useStore } from "vuex";

export default {
	components: { Breadcrumbs, CustomConfirmDialog },
	data() {
		return {
			activeTab: "outreach",
			store: useStore(),
			showCreateModal: false,
			showConfirmDialog: false,
			showNewsletterKPIs: false,
			userRole: null,

			tabs: [
				{
					key: "growth",
					label: "Growth",
					icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
				},
				{
					key: "outreach",
					label: "Outreach",
					icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
				},
			],

			statsCards: [
				{
					label: "Total Newsletters Sent",
					value: "0",
					icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
				},
				{
					label: "Emails Sent",
					value: "0",
					icon: "M12 19l9 2-9-18-9 18 9-2zm0 0v-8",
				},
				{
					label: "Audience Reached",
					value: "0",
					icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
				},
			],

			steps: [
				{
					title: "Choose Your Reach",
					desc: "Select your target audience and review the estimated recipient count.",
				},
				{
					title: "Craft the Campaign",
					desc: "Create a newsletter with a strong subject line, rich content, and call-to-actions!",
				},
				{
					title: "Preview & Review",
					desc: "Check desktop and mobile previews to ensure clarity and consistency.",
				},
				{
					title: "Deliver at Scale",
					desc: "Send your campaign securely with user consent settings automatically applied.",
				},
			],

			organizerStep: [
				{
					title: "Select Your Audience",
					desc: "Choose your eligible recipients — you'll see an estimated recipient count before sending.",
				},
				{
					title: "Create Your Message",
					desc: "Click <strong>Create Newsletter</strong>, write a powerful subject line, and design your content.",
				},
				{
					title: "Preview & Confirm",
					desc: "Review your newsletter in desktop and mobile preview to ensure everything looks right.",
				},
				{
					title: "Deliver at Scale",
					desc: "Send your campaign securely with user consent settings automatically applied.",
				},
			],

			communicationTypes: [{ value: 1, label: "Email" }],
			audienceOptions: [
				{ label: "All", value: 1 },
				{ label: "Organizers", value: 2 },
				{ label: "Attendees", value: 3 },
			],
			sendingMethod: 0,
			sendingDestination: 0,
			form: { subject: "", messageContent: "" },
		};
	},

	methods: {
		forAdmin() {
			if (!this.userRole || this.userRole.user_type === null) return false;
			const type = Number(this.userRole.user_type);
			return type !== 1 && type !== 2;
		},
		forClient() {
			if (!this.userRole || !this.userRole.user_type) return false;
			const type = Number(this.userRole.user_type);
			return type !== 1 && type === 2;
		},
		openCreateCampaignModal() {
			this.showCreateModal = true;
			this.userRole = this.store.state.auth.user || {};
		},
		closeCreateCampaignModal() {
			this.showCreateModal = false;
			this.form.subject = "";
			this.form.messageContent = "";
			this.sendingMethod = 0;
			this.sendingDestination = 0;
		},
		confirmCreateCampaign() {
			this.showConfirmDialog = true;
		},
		async createCampaign() {
			try {
				const response = await apiDataService.sendNewsLetterSubscription(
					this.sendingDestination,
					{
						subject: this.form.subject,
						message: this.form.messageContent,
						method: this.sendingMethod,
					},
				);
				showToast(
					"success",
					`${response?.data?.message || "Campaign created successfully!"}`,
				);
				this.closeCreateCampaignModal();
				this.showConfirmDialog = false;
			} catch (error) {
				showToast(
					"error",
					error.response?.data?.message ||
						"Failed to create campaign. Please try again.",
				);
				console.error("Error creating campaign:", error);
				return;
			}
		},
	},

	mounted() {
		this.userRole = this.store.state.auth.user || {};
	},
};
</script>

<style scoped>
.modal-label {
	@apply flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-widest mb-2
         text-muted dark:text-muted-light;
}
.modal-input {
	@apply w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium
         bg-primary-50/60 dark:bg-dark-600
         border border-primary-100 dark:border-dark-500
         text-dark-700 dark:text-white
         outline-none transition-all duration-200
         focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20
         dark:focus:border-primary-400;
}
.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}
</style>
